import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import MikaCard from '../../components/MikaCard/MikaCard';

interface Props {
    data: any[];
    onRowClick: (row: any) => void;
}

const getInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return (parts[0]?.charAt(0) + (parts[1]?.charAt(0) || '')).toUpperCase();
};

const getAvatarColor = (name: string): string => {
    const colors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#22c55e', '#ec4899', '#6366f1'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
};

const ReworkCardGrid: React.FC<Props> = ({ data, onRowClick }) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string, values?: Record<string, any>) =>
        intl.formatMessage({ id, defaultMessage }, values);

    return (
        <div className="ccg-grid">
            {data.length === 0 ? (
                <div className="ccg-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                        <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 105.64-11.36L1 10" />
                    </svg>
                    <p><FormattedMessage id="production.rework.empty" defaultMessage="Nuk u gjet asnjë kthim" /></p>
                </div>
            ) : (
                data.map((row, idx) => {
                    const initials = getInitials(row.clientName || '');
                    const avatarColor = getAvatarColor(row.clientName || '');
                    const isOpen = row.status === 'open';

                    return (
                        <MikaCard
                            key={idx}
                            onClick={() => onRowClick(row)}
                            header={
                                <div className="ccg-card-header">
                                    <div className="ccg-avatar" style={{ background: avatarColor }}>{initials}</div>
                                    <div className="ccg-card-name">
                                        <span className="ccg-name">{row.responsibilityName || '\u2014'}</span>
                                        <span className="ccg-company">{row.clientName}</span>
                                    </div>
                                    <span className="ccg-status-badge" style={{
                                        color: isOpen ? '#92400e' : '#166534',
                                        background: isOpen ? '#fef3c7' : '#dcfce7'
                                    }}>
                                        {isOpen
                                            ? <FormattedMessage id="production.returnHistory.open" defaultMessage="E hapur" />
                                            : <FormattedMessage id="production.returnHistory.resolved" defaultMessage="E zgjidhur" />}
                                    </span>
                                </div>
                            }
                            body={
                                <div className="ccg-card-body-content">
                                    <div className="ccg-info-grid">
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.board.productCol" defaultMessage="Produkti" /></span>
                                            <span className="ccg-info-value">{row.articleName || '\u2014'}</span>
                                        </div>
                                        {row.materialName && (
                                            <div className="ccg-info-item">
                                                <span className="ccg-info-label"><FormattedMessage id="production.responsibilityArchive.col.material" defaultMessage="Materiali" /></span>
                                                <span className="ccg-info-value">{row.materialName}</span>
                                            </div>
                                        )}
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.rework.col.returnCount" defaultMessage="Kthimet" /></span>
                                            <span className="ccg-info-value" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {row.adminReturnCount > 0 && <span style={{ fontSize: 10, color: '#ef4444', fontWeight: 600 }}>{fm('production.rework.card.returnedShort', 'Kthyer {count}x', { count: row.adminReturnCount })}</span>}
                                                {row.workerBlockCount > 0 && <span style={{ fontSize: 10, color: '#f97316', fontWeight: 600 }}>{fm('production.rework.card.blockedShort', 'Bllokuar {count}x', { count: row.workerBlockCount })}</span>}
                                            </span>
                                        </div>
                                        {row.lastCategoryName && (
                                            <div className="ccg-info-item">
                                                <span className="ccg-info-label"><FormattedMessage id="production.rework.col.category" defaultMessage="Kategoria" /></span>
                                                <span className="ccg-info-value">
                                                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: '#dbeafe', color: '#1e40af', fontWeight: 600 }}>
                                                        {row.lastCategoryName}
                                                    </span>
                                                </span>
                                            </div>
                                        )}
                                        {row.lastAssignedWorker && (
                                            <div className="ccg-info-item">
                                                <span className="ccg-info-label"><FormattedMessage id="production.responsibilityArchive.popover.worker" defaultMessage="Punëtori" /></span>
                                                <span className="ccg-info-value">{row.lastAssignedWorker}</span>
                                            </div>
                                        )}
                                        {row.lastReturnedByUser && (
                                            <div className="ccg-info-item">
                                                <span className="ccg-info-label"><FormattedMessage id="production.rework.strip.returnedBy" defaultMessage="Kthyer nga" /></span>
                                                <span className="ccg-info-value">{row.lastReturnedByUser}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            }
                            footer={
                                <div className="ccg-card-footer">
                                    <span className="ccg-date" style={{ color: '#64748b', fontSize: 11 }}>
                                        {row.lastReturnedAt || ''}
                                    </span>
                                    {row.lastReturnReason && (
                                        <span style={{ fontSize: 10, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                                            {row.lastReturnReason}
                                        </span>
                                    )}
                                </div>
                            }
                        />
                    );
                })
            )}
        </div>
    );
};

export default ReworkCardGrid;
